"use client";

import Image from "next/image";
import Link from "next/link";
import { ShoppingBag, Heart, Plus, Check, Star } from "lucide-react";
import WishlistButton from "./WishlistButton";
import { useCartStore } from "@/lib/store/useCartStore";
import { useState } from "react";
import { getEffectivePrice } from "@/lib/pricing";

interface ProductCardProps {
  product: any;
}

export default function ProductCard({ product }: ProductCardProps) {
  const addItem = useCartStore((state) => state.addItem);
  const [added, setAdded] = useState(false);
  
  const defaultVariant = product.variants.find((v: any) => v.isDefault) || product.variants[0];
  const productImages: string[] = product.images
    ? (typeof product.images === "string" ? JSON.parse(product.images) : product.images)
    : [];
  const mainImage = productImages[0] || null;
  const { hasDiscount, effectivePrice, discountPercent } = getEffectivePrice(defaultVariant);

  // Yıldız derecelendirmesi — her zaman gösterilir; yorum yoksa tüm yıldızlar pasif/gri kalır
  const reviews: { rating: number }[] = Array.isArray(product.reviews) ? product.reviews : [];
  const reviewCount = reviews.length;
  const avgRating = reviewCount ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviewCount : 0;

  const handleQuickAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!defaultVariant) return;
    
    addItem({
      id: defaultVariant.id,
      productId: product.id,
      name: product.nameTR,
      mlLabel: defaultVariant.mlLabel,
      price: effectivePrice,
      quantity: 1,
      slug: product.slug,
      image: mainImage,
      campaign: product.campaign ? (typeof product.campaign === "string" ? JSON.parse(product.campaign) : product.campaign) : null,
      kargoUcreti: defaultVariant.kargoUcreti ?? null,
    });
    
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="group relative">
      {/* Favori Butonu (Sol Üst) */}
      <div className="absolute top-4 left-4 z-20 scale-110">
        <WishlistButton
          product={{
            id: product.id,
            name: product.nameTR,
            price: effectivePrice || 0,
            slug: product.slug,
            image: mainImage
          }}
        />
      </div>

      <Link
        href={`/products/${product.slug}`}
        className="block bg-[#111114] border border-[rgba(201,168,76,0.08)] rounded-xl p-4 hover:border-[#C9A84C]/40 transition-all duration-500 shadow-lg hover:shadow-[#C9A84C]/5"
      >
        <div className="relative aspect-[9/16] bg-black/40 mb-6 overflow-hidden rounded-lg">
          {hasDiscount && (
            <div className="absolute top-3 right-3 z-10 bg-gradient-to-r from-[#C9A84C] to-[#D4B664] text-black text-[10px] font-bold px-2 py-1 rounded-full shadow-lg">
              %{discountPercent}
            </div>
          )}
          {mainImage ? (
            <Image
              src={mainImage}
              alt={product.nameTR}
              fill
              className="object-cover group-hover:scale-110 transition-transform duration-700"
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ShoppingBag size={48} className="text-[#7A7670]/20" />
            </div>
          )}
          
          {/* Hızlı Ekleme Overlay (Hoverda çıkar) */}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
            <button 
              onClick={handleQuickAdd}
              className="bg-[#C9A84C] text-black w-12 h-12 rounded-full flex items-center justify-center transform translate-y-4 group-hover:translate-y-0 transition-all duration-500 shadow-xl"
            >
              {added ? <Check size={20} strokeWidth={3} /> : <Plus size={24} strokeWidth={3} />}
            </button>
          </div>
        </div>
        
        <div className="space-y-4">
          <div className="text-center">
            <h3 className="font-serif text-lg text-[#F4F0E8] group-hover:text-[#C9A84C] transition-colors line-clamp-1">
              {product.nameTR}
            </h3>
            <div className="flex items-center justify-center gap-1 mt-1.5">
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    size={12}
                    className={i < Math.round(avgRating) ? "text-[#C9A84C]" : "text-[#7A7670]/30"}
                    fill={i < Math.round(avgRating) ? "currentColor" : "none"}
                  />
                ))}
              </div>
              {reviewCount > 0 && <span className="text-[10px] text-[#7A7670]">({reviewCount})</span>}
            </div>
          </div>
          
          <div className="pt-2 border-t border-[rgba(201,168,76,0.06)]">
            <div className="flex flex-col items-center gap-1">
              <span className="text-[10px] text-[#7A7670] uppercase tracking-widest">Başlayan fiyatlarla</span>
              <div className="flex items-center gap-2">
                <div className="font-mono text-xl font-bold text-[#F4F0E8]">
                  {defaultVariant ? `₺${effectivePrice.toLocaleString('tr-TR')}` : 'Fiyat Yok'}
                </div>
                {hasDiscount && (
                  <div className="font-mono text-xs text-[#7A7670] line-through opacity-60">
                    ₺{defaultVariant.price.toLocaleString('tr-TR')}
                  </div>
                )}
              </div>
            </div>
            
            {/* Alt İşlem Butonu */}
            <button
              onClick={handleQuickAdd}
              className={`w-full mt-4 py-3 rounded-lg flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-widest transition-all ${
                added 
                  ? "bg-green-600 text-white" 
                  : "bg-[rgba(201,168,76,0.05)] text-[#C9A84C] border border-[rgba(201,168,76,0.15)] hover:bg-[#C9A84C] hover:text-black"
              }`}
            >
              {added ? (
                <>
                  <Check size={14} />
                  Sepete Eklendi
                </>
              ) : (
                <>
                  <ShoppingBag size={14} />
                  Hızlı Ekle
                </>
              )}
            </button>
          </div>
        </div>
      </Link>
    </div>
  );
}
